import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import type { GreeningQuestionItem } from '../../services/greeningApi';
import { GreeningChatTitle } from './GreeningBrandLabel';
import { GreeningChatPanel } from './GreeningChatPanel';
import { GreeningIcon } from './GreeningIcon';
import { GREENING_QA_RETENTION_INFO, GREENING_QA_RETENTION_MONTHS } from './constants';
import { ConversarComGreeningButton } from './voice/ConversarComGreeningButton';
import { VoiceChatReveal } from './voice/voiceChatReveal';

interface ChatProps {
  items: GreeningQuestionItem[];
  loading?: boolean;
  onAsk: (question: string) => Promise<void>;
  asking?: boolean;
  disabled?: boolean;
  disabledHint?: string;
  maxHeight?: number;
}

interface DialogProps extends ChatProps {
  open: boolean;
  onClose: () => void;
  /** Id da legislação autorizada — o backend revalida. */
  referenceId?: string | null;
}

/** Chat AI Greening em modal sobreposta à modal de detalhes da mudança. */
export function GreeningChatDialog({
  open,
  onClose,
  items,
  loading,
  onAsk,
  asking,
  disabled,
  disabledHint,
  maxHeight = 480,
  referenceId,
}: DialogProps) {
  const [voiceReveal, setVoiceReveal] = useState<VoiceChatReveal | null>(null);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: 'min(85vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.25} pr={1}>
          <GreeningIcon sx={{ fontSize: 32, color: 'success.main' }} />
          <Box>
            <Typography variant="h6" component="div">
              <GreeningChatTitle />
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                Suas perguntas sobre esta norma — respostas do agente de legislação ambiental.
              </Typography>
              <Tooltip title={GREENING_QA_RETENTION_INFO} arrow placement="top">
                <IconButton size="small" aria-label={`Retenção de histórico: ${GREENING_QA_RETENTION_MONTHS} meses`} sx={{ p: 0.25 }}>
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Fechar chat">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <GreeningChatPanel
          embedded
          items={items}
          loading={loading}
          onAsk={onAsk}
          asking={asking}
          disabled={disabled}
          disabledHint={disabledHint}
          maxHeight={maxHeight}
          voiceReveal={voiceReveal}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        {referenceId ? (
          <ConversarComGreeningButton
            agentId="legislative"
            context={{ type: 'legislation', referenceId }}
            onReveal={setVoiceReveal}
          />
        ) : (
          <Box />
        )}
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GreeningChatDialog;
